import { Menu } from "@headlessui/react";
import { UserCircleIcon } from "@heroicons/react/24/solid";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { useLayoutStore } from "../../context";
import useUserStore from "../../context/useUserStore";
import { classNames } from "../../utils/helpers";
import { useNavigate } from "react-router";
import { useAlert } from "../../hooks";

/**
 * TopNavMenu — TCSB admin top navigation bar.
 * Shows brand, sidebar toggle, and user dropdown with logout.
 */
export default function TopNavMenu() {
  const { sidebarMenuOpen, setSidebarMenuOpen } = useLayoutStore();
  const { currentUser, logout } = useUserStore();

  const navigate = useNavigate();
  const alert = useAlert();

  const handleLogout = async () => {
    logout();
    navigate("/login");
    alert("Signed out", "You have been signed out.", "success");
  };

  return (
    <div className="z-50 px-4 bg-charcoal flex h-14 items-center justify-between fixed w-screen top-0 left-0 shadow-md">
      {/* Sidebar toggle */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => setSidebarMenuOpen(!sidebarMenuOpen)}
          aria-label={sidebarMenuOpen ? "Collapse sidebar" : "Expand sidebar"}
          className="text-aluminum hover:text-white transition-colors"
          data-testid="sidebar-toggle"
        >
          {sidebarMenuOpen
            ? <XMarkIcon className="h-6 w-6" />
            : <Bars3Icon className="h-6 w-6" />
          }
        </button>

        {/* Brand */}
        <Link
          to="/admin"
          className="font-heading text-lg tracking-widest text-white hover:text-mustard transition-colors"
          aria-label="Admin dashboard"
        >
          TCSB ADMIN
        </Link>
      </div>

      {/* User dropdown */}
      <Menu as="div" className="relative pe-4">
        <Menu.Button
          className="flex items-center gap-2 text-aluminum hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-mustard focus:ring-offset-2 focus:ring-offset-charcoal rounded-full"
          data-testid="user-menu-button"
        >
          <span className="sr-only">Open user menu</span>
          <UserCircleIcon className="w-9 h-9" />
          {currentUser?.username && (
            <span className="hidden sm:block font-body text-sm">
              {currentUser.username}
            </span>
          )}
        </Menu.Button>

        <Menu.Items className="absolute right-0 z-50 mt-2 w-44 origin-top-right rounded-sm bg-white shadow-lg ring-1 ring-charcoal/10 focus:outline-none">
          <Menu.Item>
            {({ active }) => (
              <Link
                to="/admin/profile"
                className={classNames(
                  active ? "bg-cream" : "",
                  "block w-full text-left px-4 py-2 text-sm font-body text-charcoal"
                )}
                data-testid="profile-link"
              >
                Profile
              </Link>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={handleLogout}
                className={classNames(
                  active ? "bg-cream" : "",
                  "block w-full text-left px-4 py-2 text-sm font-body text-charcoal"
                )}
                data-testid="logout-button"
              >
                Sign Out
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Menu>
    </div>
  );
}
